import React from 'react'
import { brand } from '../../config/settings/brand'
import routes from '../../config/settings/routes'
import LegalDocument from './LegalDocument'
import {
  InlineLink,
  List,
  MailLink,
  Paragraph,
  SectionTitle,
} from './Legal.styles'

const mailHref = `mailto:${brand.legalContactEmail}`

const Privacy = () => (
  <LegalDocument
    title="Política de Privacidad"
    description={`Cómo ${brand.name} trata los datos personales de quienes usan el servicio de seguimiento de pensión alimenticia en Chile.`}
  >
    <Paragraph>
      Esta política describe cómo {brand.name} trata los datos personales de
      quienes usan {brand.url}. El servicio se presta en {brand.legalCity} bajo
      la marca de producto {brand.name} y la marca comercial{' '}
      {brand.legalOperatorBrand}. {brand.legalOperatorBrand} no es, a la fecha,
      una persona jurídica.
    </Paragraph>
    <Paragraph>
      El responsable del tratamiento es la persona natural que opera el
      servicio. Para ejercer derechos, hacer preguntas o que te indiquemos
      quién responde, escribe a{' '}
      <MailLink href={mailHref}>{brand.legalContactEmail}</MailLink>.
    </Paragraph>

    <SectionTitle>Qué datos tratamos</SectionTitle>
    <Paragraph>
      Al entrar con Google y usar el servicio podemos tratar:
    </Paragraph>
    <List>
      <li>
        Identidad de tu cuenta de Google: nombre, correo y foto de perfil.
      </li>
      <li>
        Configuración de tu pensión alimenticia: método (UTM o % del sueldo
        mínimo), el factor de tu resolución y el día de pago.
      </li>
      <li>
        Montos estimados mes a mes, si marcaste el pago y cuándo lo hiciste.
      </li>
      <li>
        Mensajes que envías por /ayuda (texto libre) y el historial técnico
        necesario para operar la cuenta (sesión, IP y navegador en nuestros
        registros de autenticación).
      </li>
      <li>
        Datos de uso del producto (PostHog) y de tráfico del sitio (Google
        Analytics 4), asociados a tu visita o, si iniciaste sesión, a tu
        cuenta.
      </li>
    </List>

    <SectionTitle>Qué no pedimos</SectionTitle>
    <Paragraph>
      No solicitamos RUT, sueldo, cuenta bancaria, datos de quien recibe la
      pensión ni el PDF o foto de la sentencia. Tampoco vendemos tu información.
    </Paragraph>

    <SectionTitle>Para qué los usamos</SectionTitle>
    <List>
      <li>Crear y mantener tu cuenta.</li>
      <li>
        Calcular el monto estimado según tu resolución y los valores oficiales
        de UTM o IMM, y mostrarte historial.
      </li>
      <li>
        Enviarte correos del servicio: bienvenida, recordatorio si no
        terminas de configurar, monto del mes y avisos cerca del día de pago.
      </li>
      <li>Atender soporte y mejorar el producto.</li>
    </List>

    <SectionTitle>Base del tratamiento</SectionTitle>
    <Paragraph>
      Tratamos estos datos para prestar el servicio que solicitas al crear la
      cuenta (ejecución del contrato y medidas precontractuales) y con tu
      autorización al continuar con Google, según la Ley N° 19.628 sobre
      protección de la vida privada. Nos preparamos para las obligaciones de
      información y derechos de la Ley N° 21.719, que rige en plenitud desde el
      1 de diciembre de 2026.
    </Paragraph>

    <SectionTitle>Encargados y transferencias</SectionTitle>
    <Paragraph>
      Usamos proveedores que tratan datos por nuestra cuenta, algunos fuera de
      Chile (en particular Estados Unidos o la Unión Europea):
    </Paragraph>
    <List>
      <li>Google — inicio de sesión y, si aplica, Analytics.</li>
      <li>Supabase — autenticación y base de datos.</li>
      <li>Vercel — alojamiento de este sitio.</li>
      <li>Resend — envío de correos transaccionales.</li>
      <li>PostHog — analítica de producto.</li>
    </List>
    <Paragraph>
      No cedemos tu información a terceros para su marketing. Los recordatorios
      y el cálculo salen de nuestros sistemas, no de un estudio jurídico ni de
      un tribunal.
    </Paragraph>

    <SectionTitle>Conservación</SectionTitle>
    <Paragraph>
      Guardamos la cuenta mientras esté activa. Si pides la eliminación, dejamos
      de enviarte correos y borramos o anonimizamos los datos personales que ya
      no necesitemos para cumplir una obligación legal o defender un reclamo.
    </Paragraph>

    <SectionTitle>Tus derechos</SectionTitle>
    <Paragraph>
      Puedes pedir acceso, rectificación, cancelación (eliminación) u oposición
      al tratamiento. Escríbenos a{' '}
      <MailLink href={mailHref}>{brand.legalContactEmail}</MailLink> desde el
      correo de tu cuenta. Responderemos por el mismo canal. Aún no hay un
      borrado automático dentro de la aplicación; lo gestionamos a pedido.
    </Paragraph>

    <SectionTitle>Cookies y analítica</SectionTitle>
    <Paragraph>
      El sitio usa cookies o identificadores similares de Google Analytics 4
      (tráfico) y de PostHog (producto). No hay un banner de cookies en esta
      versión. Si no quieres ese seguimiento, puedes bloquear cookies de
      terceros en tu navegador o no iniciar sesión; el servicio de pensión
      requiere cuenta.
    </Paragraph>

    <SectionTitle>Correos</SectionTitle>
    <Paragraph>
      Los correos de {brand.name} son del servicio (alta, configuración, monto
      del mes, recordatorios de pago). No usamos tu dirección para publicidad
      de terceros. Si no creaste esta cuenta, ignora el mensaje y avísanos.
    </Paragraph>

    <SectionTitle>Seguridad</SectionTitle>
    <Paragraph>
      El acceso es con tu cuenta de Google. Los datos de pensión viven en
      Supabase con control de acceso por fila. Ninguna medida es infalible;
      si hay un incidente que afecte tus datos, te avisaremos cuando corresponda.
    </Paragraph>

    <SectionTitle>Cambios</SectionTitle>
    <Paragraph>
      Si cambia esta política de forma relevante, actualizaremos la fecha de
      esta página. El uso continuado del servicio después de esa fecha implica
      que tomaste conocimiento de la versión vigente.
    </Paragraph>

    <SectionTitle>Términos</SectionTitle>
    <Paragraph>
      El uso del servicio también se rige por los{' '}
      <InlineLink to={routes.terms}>Términos y Condiciones</InlineLink>.
    </Paragraph>
  </LegalDocument>
)

export default Privacy
